import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, AlertTriangle, HeartHandshake, BookOpen } from "lucide-react";
import SEO from "@/components/SEO";
import { THERAPISTS, BOOKING_URL } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";
import { fetchPublicPosts } from "@/lib/api";

// C8 — Pregnancy resource hub.
// Hybrid: hand-written copy here + auto-pulled blog posts categorised
// "Pregnancy Discomfort" (or similar) + AMTA external citation.

const TRIMESTER_GUIDANCE = [
  {
    name: "First Trimester",
    description:
      "Many therapists recommend waiting until the second trimester before starting pregnancy massage, especially in higher-risk pregnancies. We follow your obstetrician's guidance — get clearance first, then we'll work with you.",
  },
  {
    name: "Second Trimester",
    description:
      "Usually the most comfortable window for pregnancy massage. Side-lying positioning, gentle pressure and lower-back focus — designed to ease the changes your body is going through.",
  },
  {
    name: "Third Trimester",
    description:
      "Sessions become shorter and more supportive. We focus on lower back, hips, legs and shoulders, with careful positioning and pillows. Comfort is the priority.",
  },
  {
    name: "Post-Natal Recovery",
    description:
      "After birth, gentle massage may help support recovery, ease shoulder and neck tension from feeding, and aid the return to your usual activity. We follow your physician's clearance.",
  },
];

const MAY_HELP_WITH = [
  "Lower-back and hip discomfort",
  "Neck and shoulder tension from feeding & posture changes",
  "Sciatic-type referral patterns",
  "Restless sleep & general stress",
  "Swelling in the legs and feet (gentle techniques)",
  "Post-natal recovery support",
];

const SAFETY_CONSIDERATIONS = [
  "Always get clearance from your obstetrician or GP first.",
  "Higher-risk pregnancies, gestational hypertension or pre-eclampsia require explicit clearance before we proceed.",
  "Severe morning sickness, bleeding, or recent placental complications mean we'll pause until your physician advises otherwise.",
  "We avoid certain reflex zones during pregnancy and adjust techniques based on trimester and your comfort.",
];

const PREG_THERAPISTS = THERAPISTS.filter((t) =>
  (t.specialties || []).some((s) => /pregnancy/i.test(s))
);

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
    { "@type": "ListItem", position: 2, name: "Pregnancy Resource Hub", item: absoluteUrl("/pregnancy") },
  ],
};

export default function Pregnancy() {
  const [related, setRelated] = useState([]);

  useEffect(() => {
    fetchPublicPosts()
      .then((rows) => {
        const filtered = (Array.isArray(rows) ? rows : [])
          .filter((p) => /pregnan/i.test(p.category || "") || /pregnan/i.test(p.title || ""))
          .slice(0, 3);
        setRelated(filtered);
      })
      .catch(() => setRelated([]));
  }, []);

  return (
    <main data-testid="pregnancy-page" className="bg-white">
      <SEO
        title="Pregnancy massage — Ashlan Clinic, Bermuda"
        description="A safety-first resource hub for pregnancy massage in Bermuda. Trimester guidance, what we may help with, our pregnancy-trained therapists and trusted references."
        path="/pregnancy"
        schemas={[breadcrumb]}
      />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <span className="ash-eyebrow">Pregnancy · Resource Hub</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#080808] max-w-4xl">
            Care for your body through pregnancy, with safety first.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3C4048] max-w-3xl leading-relaxed">
            Pregnancy massage at Ashlan Clinic is delivered by therapists trained for the specific changes
            your body is going through. Soft language, careful positioning, no spa fluff.
          </p>
          <div className="mt-8 border-l-4 border-[#FBBF24] bg-[#FEF3C7] p-5 max-w-3xl" data-testid="pregnancy-disclaimer">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-[#92400E] shrink-0 mt-0.5" />
              <p className="text-sm text-[#78350F] leading-relaxed">
                <strong>Get clearance from your obstetrician or GP first.</strong> This page shares what
                pregnancy massage <em>may help</em> with, what it does not, and when to wait. It is not a
                medical recommendation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <HeartHandshake size={20} className="text-[#3A7A8C]" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">Trimester guidance</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="trimesters">
            {TRIMESTER_GUIDANCE.map((t) => (
              <div key={t.name} className="bg-white border border-[#e5e7eb] p-6 flex flex-col" data-testid={`trimester-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <h3 className="text-lg font-bold text-[#080808]">{t.name}</h3>
                <p className="mt-3 text-sm text-[#3C4048] leading-relaxed">{t.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <span className="ash-eyebrow">What it may help with</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
              Common discomforts of pregnancy.
            </h2>
            <ul className="mt-8 flex flex-col gap-3" data-testid="may-help-list">
              {MAY_HELP_WITH.map((m) => (
                <li key={m} className="flex gap-3 text-[#3C4048] leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5FA8B4] mt-2.5 shrink-0" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-l-4 border-[#5FA8B4] bg-[#F0F7F9] p-8" data-testid="safety-considerations">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={20} className="text-[#3A7A8C]" />
              <span className="ash-eyebrow">Safety considerations</span>
            </div>
            <ul className="flex flex-col gap-3">
              {SAFETY_CONSIDERATIONS.map((s) => (
                <li key={s} className="text-sm text-[#3C4048] leading-relaxed">— {s}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {PREG_THERAPISTS.length > 0 && (
        <section className="py-16 md:py-20 bg-[#F5F5F5]" data-testid="preg-therapists-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <span className="ash-eyebrow">Our pregnancy-trained therapists</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
              The people who&apos;ll care for you.
            </h2>
            <div className="mt-10 grid md:grid-cols-2 gap-8">
              {PREG_THERAPISTS.map((t) => (
                <div key={t.slug} className="bg-white border border-[#e5e7eb] p-6 flex gap-5" data-testid={`preg-therapist-${t.slug}`}>
                  <img src={t.photo} alt={t.name} className="w-28 h-28 object-cover flex-shrink-0" loading="lazy" />
                  <div>
                    <h3 className="text-xl font-bold text-[#080808]">{t.name}</h3>
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3A7A8C] mt-1">{t.role}</p>
                    <p className="mt-3 text-sm text-[#3C4048] leading-relaxed line-clamp-3">{t.bio}</p>
                    <a href={t.bookingUrl} className="ash-link-underline mt-3 inline-flex items-center gap-1.5">
                      Book with {t.name.split(" ")[0]} <ArrowUpRight size={12} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="py-16 md:py-20 bg-white" data-testid="preg-related-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-8">
              <BookOpen size={20} className="text-[#3A7A8C]" />
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">Related reading from our journal</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`preg-related-${p.slug}`}
                  className="block bg-white border border-[#e5e7eb] hover:border-[#3A7A8C] hover:-translate-y-0.5 transition-all duration-200">
                  {p.featured_image_url && (
                    <div className="aspect-[16/10] overflow-hidden bg-[#F5F5F5]">
                      <img src={p.featured_image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3A7A8C]">{p.category}</div>
                    <h3 className="mt-2 text-lg font-bold text-[#080808] leading-tight">{p.title}</h3>
                    {p.excerpt && <p className="mt-2 text-sm text-[#3C4048] line-clamp-3">{p.excerpt}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-24 bg-[#080808] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#9DD4D8]">Ready when you are</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            Care delivered with patience and precision.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#cbd5e1] leading-relaxed">
            Once you have clearance from your physician, book directly with one of our pregnancy-trained therapists.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={BOOKING_URL} data-testid="preg-book-now" className="ash-btn-primary !bg-white !text-[#080808] hover:!bg-[#9DD4D8]">
              Book a pregnancy session <ArrowUpRight size={16} />
            </a>
            <Link to="/contact?subject=Pregnancy%20massage%20question" className="ash-link-underline text-white hover:text-[#9DD4D8]" data-testid="preg-contact">
              Ask a question first <ArrowRight size={14} />
            </Link>
          </div>
          <p className="mt-12 text-xs text-[#94a3b8] leading-relaxed">
            Further reading:{" "}
            <a href="https://www.amtamassage.org/publications/massage-therapy-journal/pregnancy-massage-overview/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              American Massage Therapy Association — Pregnancy Massage Overview
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}
